"use client";

import {DialogFragment, DialogFragmentProps, DialogFragmentState, PageFragment} from "@/shared/PageFragment";
import {Typography} from "@mui/material";
import {FormGenContainer, FormGenContainerMode} from "@/shared/FormGenContainer";
import {PAGE_FRAGMENT_LG_WIDTH, PD_MD} from "@/shared/dimens";
import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "@/shared/BaseApp";
import React from "react";
import {
  BaseComposeMessaging,
  Pages,
  PatientProfile,
  PracticeData,
  PracticePageProfile,
  ProviderComposeSms,
  ProviderComposeSmsTemplateVariablesFactory,
  ProviderComposeWhatsApp,
  ProviderComposeWhatsAppTemplateVariablesFactory,
  ProviderProfile, StaffProfile
} from "@/libwyze-shared/types";
import {MessageSentHelper} from "./MessageSentHelper";
import {Api} from "@/libwyze-shared/api";
import {MESSAGING_SEND_CHANNEL_KTM, MessagingSendChannel} from "@/libwyze-common/api/types";
import {isValidPhoneNumber} from "libphonenumber-js";
import {StyledBoxColumn} from "@/shared/StyledComponents";

export class EditProviderMessagingHelper {

  static addProviderSms(patient?: PatientProfile) {
    const provider = PracticeData.provider().myProfile();
    const practice = Pages.getInstance().getListItem(PracticeData.provider().practiceId).practicePageProfile;
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditProviderSmsFragment
        provider={provider}
        patient={patient}
        practice={practice}
        onMessagingSent={() => {
          BaseApp.CONTEXT.hideDialog();
          setTimeout(() => {
            MessageSentHelper.showSentDialog();
          }, 100);
        }}/>);
  }

  static addProviderWhatsApp(patient?: PatientProfile) {
    const provider = PracticeData.provider().myProfile();
    const practice = Pages.getInstance().getListItem(PracticeData.provider().practiceId).practicePageProfile;
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditProviderWhatsAppFragment
        provider={provider}
        patient={patient}
        practice={practice}
        onMessagingSent={() => {
          BaseApp.CONTEXT.hideDialog();
          setTimeout(() => {
            MessageSentHelper.showSentDialog();
          }, 100);
        }}/>);
  }
}

type BaseEditProviderMessagingFragmentProps = DialogFragmentProps & {
  provider: ProviderProfile | StaffProfile,
  practice: PracticePageProfile,
  patient?: PatientProfile,
  onMessagingSent: () => void,
}

type BaseEditProviderMessagingFragmentState<T extends BaseComposeMessaging> = DialogFragmentState & {
  patientId: string,
  patient?: PatientProfile,
  message: T,
  errorMessage?: string,
}

abstract class BaseEditProviderMessagingFragment<T extends BaseComposeMessaging> extends DialogFragment<BaseEditProviderMessagingFragmentProps, BaseEditProviderMessagingFragmentState<T>> {

  protected abstract getChannel(): MessagingSendChannel;

  protected onCreateState(props?: BaseEditProviderMessagingFragmentProps): BaseEditProviderMessagingFragmentState<T> {
    return {
      ...super.onCreateState(),
      title: `Send ${MESSAGING_SEND_CHANNEL_KTM.findMetadata(this.getChannel()).text}`,
      patientId: props.patient?.id,
      message: this.createMessage(props.provider, props.patient, props.practice),
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.setState({
      patient: this.props.patient,
    });
  }

  componentDidUpdate(prevProps: Readonly<BaseEditProviderMessagingFragmentProps>, prevState: Readonly<BaseEditProviderMessagingFragmentState<T>>, snapshot?: any) {
    const patientId = this.state.patientId;
    if (patientId && patientId !== prevState.patientId) {
      this.updatePatient(patientId);
    }
    if (this.state.patient?.id && (this.state.patient.id !== prevState.patient?.id)) {
      this.setState({
        errorMessage: !isValidPhoneNumber(this.state.patient.basicInfo.phoneNumber) ? "You cannot send this message as the patient's phone number is invalid." : null,
      });
    }
  }

  protected abstract createMessage(provider: ProviderProfile | StaffProfile, patient: PatientProfile, practice: PracticePageProfile): T;

  private async updatePatient(patientId: string): Promise<void> {
    const patient = await PracticeData.provider().patientProfiles.getOrLoadItem(patientId);
    this.setState({
      patient: patient,
      message: this.createMessage(this.props.provider, patient, this.props.practice),
    });
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return "Send";
  }

  setActionButtonEnabled(enabled: boolean) {
    super.setActionButtonEnabled(enabled && !this.state.errorMessage);
  }

  onActionButtonClicked() {
    this.setActionButtonEnabled(false);
    this.sendMessage()
      .then(() => this.props.onMessagingSent())
      .catch(() => {
        this.setActionButtonEnabled(true);
        BaseApp.CONTEXT.showToast("Oops, something went wrong. Please try again later!");
      })
  }

  private async sendMessage(): Promise<void> {
    const message = this.state.message;
    const templateId = message.templateId;
    if (!templateId) {
      return;
    }
    await Api.getInstance().sendMessaging({
      channel: this.getChannel(),
      fromId: "noreply_dentwyze_in",
      to: this.state.patient.basicInfo.phoneNumber,
      templateId: templateId,
      templateVariables: message.factory.getTemplateVariables(templateId),
    });
    // await Api.getInstance().notifyEvent<NotificationEventRequest<NotificationDataNewMessage>>({
    //   type: NotificationEventType.PRACTICE_MESSAGES_NEW_MESSAGE,
    //   practiceId: PracticeData.shared().practiceId,
    //   data: {
    //     toId: message.toId,
    //     message: `[${MESSAGING_SEND_CHANNEL_KTM.findMetadata(this.getChannel()).text}] ` + message.message,
    //   },
    // });
  }

  renderContent(): React.ReactElement | null {
    return <StyledBoxColumn style={{padding: PD_MD, width: "84dvw", maxWidth: PAGE_FRAGMENT_LG_WIDTH}}>
      <FormGenContainer
        mode={this.props.patient ? FormGenContainerMode.EDIT : FormGenContainerMode.CREATE}
        content={this.state.message}
        onContainerDidChange={() => {
          this.setState({patientId: this.state.message.toId});}}
        onContainerSave={() => this.onActionButtonClicked()}
        onContainerRequiredItemsFilledChanged={(container, filled) => this.setActionButtonEnabled(filled)}/>
      {this.state.errorMessage && <Typography variant={"body2"} color="error">{this.state.errorMessage}</Typography>}
    </StyledBoxColumn>;
  }
}

class EditProviderSmsFragment extends BaseEditProviderMessagingFragment<ProviderComposeSms> {

  protected createMessage(provider: ProviderProfile | StaffProfile, patient: PatientProfile, practice: PracticePageProfile): ProviderComposeSms {
    return new ProviderComposeSms(new ProviderComposeSmsTemplateVariablesFactory(provider, patient, practice), patient?.id);
  }

  protected getChannel(): MessagingSendChannel {
    return MessagingSendChannel.SMS;
  }
}

class EditProviderWhatsAppFragment extends BaseEditProviderMessagingFragment<ProviderComposeWhatsApp> {

  protected createMessage(provider: ProviderProfile | StaffProfile, patient: PatientProfile, practice: PracticePageProfile): ProviderComposeWhatsApp {
    return new ProviderComposeWhatsApp(new ProviderComposeWhatsAppTemplateVariablesFactory(provider, patient, practice), patient?.id);
  }

  protected getChannel(): MessagingSendChannel {
    return MessagingSendChannel.WHATSAPP;
  }
}